// src/pages/admin/analytics/components/ProductFoodCostReportTab.jsx
import { useState, useMemo } from 'react';
import {
  Download,
  FileSpreadsheet,
  Printer,
  Search,
  Filter,
  RefreshCw,
  FileText,
  DollarSign,
  PieChart,
} from 'lucide-react';
import { jsPDF } from 'jspdf';
import autoTable from 'jspdf-autotable';

const ProductFoodCostReportTab = ({
  data,
  loading,
  branches = [],
  selectedBranch = '',
  setSelectedBranch,
  timeRange = 'all',
  setTimeRange,
  startDate = '',
  setStartDate,
  endDate = '',
  setEndDate,
  onRefresh,
}) => {
  const [searchTerm, setSearchTerm] = useState('');
  const [selectedCategory, setSelectedCategory] = useState('');

  const products = data?.products || [];
  const summary = data?.summary || {};

  // Get unique categories list
  const categoriesList = useMemo(() => {
    const cats = new Set();
    products.forEach((p) => {
      if (p.category) cats.add(p.category);
    });
    return Array.from(cats);
  }, [products]);

  // Filtered Products
  const filteredProducts = useMemo(() => {
    return products.filter((p) => {
      const matchSearch =
        (p.productName || '').toLowerCase().includes(searchTerm.toLowerCase()) ||
        (p.productCode || p.sku || '').toLowerCase().includes(searchTerm.toLowerCase());

      const matchCategory = !selectedCategory || p.category === selectedCategory;

      return matchSearch && matchCategory;
    });
  }, [products, searchTerm, selectedCategory]);

  // Computed Totals for Summary Row & KPI Cards
  const totals = useMemo(() => {
    const hasCategoryOrSearch = Boolean(searchTerm || selectedCategory);

    if (!hasCategoryOrSearch && summary && summary.totalSales !== undefined) {
      const totalSalesValue = Number(summary.totalSales || summary.totalRevenue || 0);
      const totalFoodCost = Number(summary.totalFoodCost || summary.totalCOGS || 0);
      const totalGrossProfit = Number(summary.totalGrossProfit ?? (totalSalesValue - totalFoodCost));
      const totalQty = filteredProducts.reduce((sum, p) => sum + (p.unitsSold || 0), 0);
      const avgSellingPrice = totalQty > 0 ? totalSalesValue / totalQty : 0;
      const avgRecipeCost = totalQty > 0 ? totalFoodCost / totalQty : 0;
      const overallGP = totalSalesValue > 0 ? (totalGrossProfit / totalSalesValue) * 100 : 0;
      const overallFoodCostPct = totalSalesValue > 0 ? (totalFoodCost / totalSalesValue) * 100 : 0;

      return {
        totalQty,
        avgSellingPrice,
        totalSalesValue,
        avgRecipeCost,
        totalFoodCost,
        totalGrossProfit,
        overallGP,
        overallFoodCostPct,
      };
    }

    const totalQty = filteredProducts.reduce((sum, p) => sum + (p.unitsSold || 0), 0);
    const totalSalesValue = filteredProducts.reduce((sum, p) => sum + (p.totalRevenue || p.salesValue || 0), 0);
    const totalFoodCost = filteredProducts.reduce((sum, p) => sum + (p.totalCOGS || p.totalFoodCost || 0), 0);
    const totalGrossProfit = filteredProducts.reduce(
      (sum, p) =>
        sum +
        (p.grossProfit ??
          (p.totalRevenue || p.salesValue || 0) - (p.totalCOGS || p.totalFoodCost || 0)),
      0
    );

    const avgSellingPrice = totalQty > 0 ? totalSalesValue / totalQty : 0;
    const avgRecipeCost = totalQty > 0 ? totalFoodCost / totalQty : 0;
    const overallGP = totalSalesValue > 0 ? (totalGrossProfit / totalSalesValue) * 100 : 0;
    const overallFoodCostPct = totalSalesValue > 0 ? (totalFoodCost / totalSalesValue) * 100 : 0;

    return {
      totalQty,
      avgSellingPrice,
      totalSalesValue,
      avgRecipeCost,
      totalFoodCost,
      totalGrossProfit,
      overallGP,
      overallFoodCostPct,
    };
  }, [filteredProducts, summary, searchTerm, selectedCategory]);

  // -------------------------------------------------------------
  // CSV Export Function
  // -------------------------------------------------------------
  const handleExportCSV = () => {
    if (filteredProducts.length === 0) return;

    const headers = [
      'Product Code',
      'Product Name',
      'Category',
      'Qty Sold',
      'Selling Price (Rs.)',
      'Sales Value (Rs.)',
      'Recipe Cost per Unit (Rs.)',
      'Total Food Cost (Rs.)',
      'Gross Profit (Rs.)',
      'GP %',
      'Food Cost %',
    ];

    const rows = filteredProducts.map((p) => [
      `"${p.productCode || p.sku || 'N/A'}"`,
      `"${p.productName || ''}"`,
      `"${p.category || 'General'}"`,
      p.unitsSold || 0,
      (p.avgSellingPrice || 0).toFixed(2),
      (p.totalRevenue || 0).toFixed(2),
      (p.unitCost || p.recipeCost || 0).toFixed(2),
      (p.totalCOGS || 0).toFixed(2),
      (p.grossProfit || 0).toFixed(2),
      `${(p.gpPct ?? (p.totalRevenue > 0 ? (p.grossProfit / p.totalRevenue) * 100 : 0)).toFixed(2)}%`,
      `${(p.foodCostPct ?? (p.totalRevenue > 0 ? (p.totalCOGS / p.totalRevenue) * 100 : 0)).toFixed(2)}%`,
    ]);

    // Summary Row
    rows.push([
      '"TOTAL / AVERAGE"',
      '""',
      '""',
      totals.totalQty,
      totals.avgSellingPrice.toFixed(2),
      totals.totalSalesValue.toFixed(2),
      totals.avgRecipeCost.toFixed(2),
      totals.totalFoodCost.toFixed(2),
      totals.totalGrossProfit.toFixed(2),
      `${totals.overallGP.toFixed(2)}%`,
      `${totals.overallFoodCostPct.toFixed(2)}%`,
    ]);

    const csvContent =
      'data:text/csv;charset=utf-8,' +
      [headers.join(','), ...rows.map((r) => r.join(','))].join('\n');

    const encodedUri = encodeURI(csvContent);
    const link = document.createElement('a');
    link.setAttribute('href', encodedUri);
    link.setAttribute(
      'download',
      `Product_Food_Cost_Profitability_Report_${new Date().toISOString().split('T')[0]}.csv`
    );
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
  };

  // -------------------------------------------------------------
  // PDF Export Function
  // -------------------------------------------------------------
  const handleExportPDF = () => {
    if (filteredProducts.length === 0) return;

    const doc = new jsPDF('landscape', 'pt', 'a4');

    // Company Header Title
    doc.setFontSize(18);
    doc.setFont('helvetica', 'bold');
    doc.text('TEZLAA CAFE - Product Food Cost & Profitability Report', 40, 40);

    doc.setFontSize(10);
    doc.setFont('helvetica', 'normal');
    const dateStr = `Generated on: ${new Date().toLocaleDateString()} | Period: ${
      startDate || 'All Time'
    } to ${endDate || 'Today'}`;
    doc.text(dateStr, 40, 58);

    const tableHeaders = [
      [
        'Product Code',
        'Product Name',
        'Category',
        'Qty Sold',
        'Selling Price (Rs.)',
        'Sales Value (Rs.)',
        'Recipe Cost (Rs.)',
        'Total Food Cost (Rs.)',
        'Gross Profit (Rs.)',
        'GP %',
        'Food Cost %',
      ],
    ];

    const tableRows = filteredProducts.map((p) => [
      p.productCode || p.sku || 'N/A',
      p.productName || '',
      p.category || 'General',
      p.unitsSold || 0,
      `Rs. ${(p.avgSellingPrice || 0).toFixed(2)}`,
      `Rs. ${(p.totalRevenue || 0).toFixed(2)}`,
      `Rs. ${(p.unitCost || p.recipeCost || 0).toFixed(2)}`,
      `Rs. ${(p.totalCOGS || 0).toFixed(2)}`,
      `Rs. ${(p.grossProfit || 0).toFixed(2)}`,
      `${(p.gpPct ?? (p.totalRevenue > 0 ? (p.grossProfit / p.totalRevenue) * 100 : 0)).toFixed(1)}%`,
      `${(p.foodCostPct ?? (p.totalRevenue > 0 ? (p.totalCOGS / p.totalRevenue) * 100 : 0)).toFixed(1)}%`,
    ]);

    // Footer Totals Row
    tableRows.push([
      'TOTAL / AVG',
      '--',
      '--',
      (totals.totalQty || 0).toString(),
      `Rs. ${(totals.avgSellingPrice || 0).toFixed(2)}`,
      `Rs. ${(totals.totalSalesValue || 0).toFixed(2)}`,
      `Rs. ${(totals.avgRecipeCost || 0).toFixed(2)}`,
      `Rs. ${(totals.totalFoodCost || 0).toFixed(2)}`,
      `Rs. ${(totals.totalGrossProfit || 0).toFixed(2)}`,
      `${(totals.overallGP || 0).toFixed(1)}%`,
      `${(totals.overallFoodCostPct || 0).toFixed(1)}%`,
    ]);

    const tableConfig = {
      head: tableHeaders,
      body: tableRows,
      startY: 75,
      styles: { fontSize: 8, cellPadding: 5 },
      headStyles: { fillColor: [30, 41, 59], textColor: [255, 255, 255], fontStyle: 'bold' },
      footStyles: { fillColor: [241, 245, 249], textColor: [15, 23, 42], fontStyle: 'bold' },
      alternateRowStyles: { fillColor: [248, 250, 252] },
    };

    if (typeof autoTable === 'function') {
      autoTable(doc, tableConfig);
    } else if (typeof doc.autoTable === 'function') {
      doc.autoTable(tableConfig);
    } else if (autoTable && typeof autoTable.default === 'function') {
      autoTable.default(doc, tableConfig);
    }

    doc.save(`Product_Food_Cost_Report_${new Date().toISOString().split('T')[0]}.pdf`);
  };

  // -------------------------------------------------------------
  // Print Function
  // -------------------------------------------------------------
  const handlePrint = () => {
    window.print();
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center py-20 text-gray-500">
        <RefreshCw className="h-8 w-8 animate-spin text-orange-500 mr-3" />
        <span className="font-medium text-lg">Generating Food Cost & Profitability Report...</span>
      </div>
    );
  }

  return (
    <div className="space-y-6 print:p-0">
      {/* Top Header & Actions Bar */}
      <div className="bg-white rounded-2xl p-6 border border-gray-200 shadow-sm flex flex-col lg:flex-row lg:items-center justify-between gap-4 print:hidden">
        <div>
          <h2 className="text-xl font-black text-gray-900 flex items-center">
            <FileText className="h-5 w-5 text-orange-500 mr-2" />
            Product Food Cost & Profitability Report
          </h2>
          <p className="text-xs text-gray-500 mt-1">
            Detailed breakdown of menu selling prices, recipe costs, sales revenues, Gross Profit (GP %), and Food Cost % metrics.
          </p>
        </div>

        {/* Download & Print Buttons */}
        <div className="flex flex-wrap items-center gap-3">
          <button
            onClick={handleExportCSV}
            className="flex items-center space-x-2 px-4 py-2 bg-emerald-600 hover:bg-emerald-700 text-white rounded-xl text-xs font-bold shadow-sm transition-all cursor-pointer"
          >
            <FileSpreadsheet className="h-4 w-4" />
            <span>Download CSV</span>
          </button>

          <button
            onClick={handleExportPDF}
            className="flex items-center space-x-2 px-4 py-2 bg-red-600 hover:bg-red-700 text-white rounded-xl text-xs font-bold shadow-sm transition-all cursor-pointer"
          >
            <Download className="h-4 w-4" />
            <span>Download PDF</span>
          </button>

          <button
            onClick={handlePrint}
            className="flex items-center space-x-2 px-4 py-2 bg-gray-900 hover:bg-gray-800 text-white rounded-xl text-xs font-bold shadow-sm transition-all cursor-pointer"
          >
            <Printer className="h-4 w-4" />
            <span>Print Report</span>
          </button>
        </div>
      </div>

      {/* Filter & Search Bar */}
      <div className="bg-white rounded-xl p-4 border border-gray-200 shadow-sm flex flex-col gap-4 print:hidden">
        <div className="flex flex-wrap items-center justify-between gap-3 w-full">
          {/* Branch & Date Range Controls */}
          <div className="flex flex-wrap items-center gap-3">
            {/* Branch Selector */}
            {setSelectedBranch && (
              <select
                value={selectedBranch}
                onChange={(e) => setSelectedBranch(e.target.value)}
                className="px-3 py-1.5 border border-gray-300 rounded-lg bg-white font-semibold text-gray-800 text-xs focus:ring-2 focus:ring-orange-500 cursor-pointer"
              >
                <option value="">🏢 All Branches</option>
                {branches.map((b) => (
                  <option key={b._id || b.id} value={b._id || b.id}>
                    📍 {b.name}
                  </option>
                ))}
              </select>
            )}

            {/* Time Range Preset Buttons */}
            {setTimeRange && (
              <div className="flex items-center bg-gray-100 p-1 rounded-xl border border-gray-200 text-xs font-semibold text-gray-700">
                <button
                  onClick={() => setTimeRange('all')}
                  className={`px-3 py-1.5 rounded-lg transition-all ${
                    timeRange === 'all' ? 'bg-white text-gray-900 shadow-sm font-bold' : 'hover:text-gray-900'
                  }`}
                >
                  All Time
                </button>
                <button
                  onClick={() => setTimeRange('today')}
                  className={`px-3 py-1.5 rounded-lg transition-all ${
                    timeRange === 'today' ? 'bg-white text-gray-900 shadow-sm font-bold' : 'hover:text-gray-900'
                  }`}
                >
                  Today
                </button>
                <button
                  onClick={() => setTimeRange('week')}
                  className={`px-3 py-1.5 rounded-lg transition-all ${
                    timeRange === 'week' ? 'bg-white text-gray-900 shadow-sm font-bold' : 'hover:text-gray-900'
                  }`}
                >
                  This Week
                </button>
                <button
                  onClick={() => setTimeRange('month')}
                  className={`px-3 py-1.5 rounded-lg transition-all ${
                    timeRange === 'month' ? 'bg-white text-gray-900 shadow-sm font-bold' : 'hover:text-gray-900'
                  }`}
                >
                  This Month
                </button>
                <button
                  onClick={() => setTimeRange('custom')}
                  className={`px-3 py-1.5 rounded-lg transition-all ${
                    timeRange === 'custom' ? 'bg-white text-gray-900 shadow-sm font-bold' : 'hover:text-gray-900'
                  }`}
                >
                  Custom
                </button>
              </div>
            )}

            {/* Date Pickers */}
            {timeRange === 'custom' && setStartDate && setEndDate && (
              <div className="flex items-center space-x-2 text-xs">
                <input
                  type="date"
                  value={startDate}
                  onChange={(e) => setStartDate(e.target.value)}
                  className="px-2.5 py-1.5 border border-gray-300 rounded-lg text-xs"
                />
                <span className="text-gray-400">to</span>
                <input
                  type="date"
                  value={endDate}
                  onChange={(e) => setEndDate(e.target.value)}
                  className="px-2.5 py-1.5 border border-gray-300 rounded-lg text-xs"
                />
              </div>
            )}
          </div>

          {/* Search Box & Category */}
          <div className="flex flex-wrap items-center gap-3">
            {/* Search Box */}
            <div className="relative w-full sm:w-64">
              <Search className="h-4 w-4 absolute left-3 top-2.5 text-gray-400" />
              <input
                type="text"
                placeholder="Search Product Code or Name..."
                value={searchTerm}
                onChange={(e) => setSearchTerm(e.target.value)}
                className="w-full pl-9 pr-3 py-1.5 text-xs border border-gray-300 rounded-lg focus:ring-2 focus:ring-orange-500 focus:border-orange-500"
              />
            </div>

            {/* Category Dropdown */}
            <div className="flex items-center space-x-2 text-xs">
              <span className="font-semibold text-gray-500 flex items-center">
                <Filter className="h-3.5 w-3.5 mr-1" /> Category:
              </span>
              <select
                value={selectedCategory}
                onChange={(e) => setSelectedCategory(e.target.value)}
                className="px-3 py-1.5 border border-gray-300 rounded-lg bg-white font-semibold text-gray-800 focus:ring-2 focus:ring-orange-500 focus:border-orange-500 cursor-pointer"
              >
                <option value="">All Categories ({categoriesList.length})</option>
                {categoriesList.map((cat) => (
                  <option key={cat} value={cat}>
                    {cat}
                  </option>
                ))}
              </select>
            </div>
          </div>
        </div>

        <div className="flex items-center justify-between text-xs text-gray-500 font-semibold pt-2 border-t border-gray-100">
          <div>
            Active Filter Period:{' '}
            <span className="text-gray-900 font-bold">
              {timeRange === 'all'
                ? 'All Time'
                : `${startDate || 'Start'} to ${endDate || 'End'}`}
            </span>
          </div>
          <div>
            Showing <span className="text-gray-900 font-bold">{filteredProducts.length}</span> of{' '}
            <span className="text-gray-900 font-bold">{products.length}</span> products
          </div>
        </div>
      </div>

      {/* Summary KPI Cards Header */}
      <div className="grid grid-cols-1 md:grid-cols-4 gap-4 print:hidden">
        <div className="bg-white rounded-xl p-4 border border-gray-200 shadow-sm">
          <p className="text-xs font-semibold text-gray-500 uppercase">Total Sales Value</p>
          <p className="text-2xl font-black text-gray-900 mt-1">
            Rs. {totals.totalSalesValue.toLocaleString('en-US', { minimumFractionDigits: 2 })}
          </p>
        </div>

        <div className="bg-white rounded-xl p-4 border border-slate-200 bg-slate-50/50 shadow-sm">
          <p className="text-xs font-semibold text-slate-600 uppercase">Total Food Cost (COGS)</p>
          <p className="text-2xl font-black text-slate-800 mt-1">
            Rs. {totals.totalFoodCost.toLocaleString('en-US', { minimumFractionDigits: 2 })}
          </p>
        </div>

        <div className="bg-white rounded-xl p-4 border border-emerald-200 bg-emerald-50/40 shadow-sm">
          <p className="text-xs font-semibold text-emerald-700 uppercase">Overall Gross Profit</p>
          <p className="text-2xl font-black text-emerald-600 mt-1">
            Rs. {totals.totalGrossProfit.toLocaleString('en-US', { minimumFractionDigits: 2 })}
          </p>
        </div>

        <div className="bg-white rounded-xl p-4 border border-amber-200 bg-amber-50/40 shadow-sm">
          <p className="text-xs font-semibold text-amber-800 uppercase">Average Food Cost %</p>
          <p className="text-2xl font-black text-amber-900 mt-1">{totals.overallFoodCostPct.toFixed(2)}%</p>
          <p className="text-[10px] text-amber-700 mt-0.5">GP Margin: {totals.overallGP.toFixed(2)}%</p>
        </div>
      </div>

      {/* Printable Report Title (Only visible during print) */}
      <div className="hidden print:block mb-6 text-center">
        <h1 className="text-2xl font-black text-gray-900">TEZLAA CAFE - Food Cost & Profitability Report</h1>
        <p className="text-xs text-gray-600 mt-1">
          Date Period: {startDate || 'All Time'} to {endDate || 'Today'} | Printed on: {new Date().toLocaleDateString()}
        </p>
      </div>

      {/* 11-Column Report Data Table */}
      <div className="bg-white rounded-xl shadow-md border border-gray-200 overflow-hidden print:shadow-none print:border-none">
        <div className="overflow-x-auto">
          <table className="w-full text-left text-xs border-collapse">
            <thead className="bg-slate-900 text-white font-bold uppercase tracking-wider text-[11px] print:bg-gray-200 print:text-black">
              <tr>
                <th className="p-3">Product Code</th>
                <th className="p-3">Product Name</th>
                <th className="p-3">Category</th>
                <th className="p-3 text-center">Qty Sold</th>
                <th className="p-3 text-right">Selling Price (Rs.)</th>
                <th className="p-3 text-right">Sales Value (Rs.)</th>
                <th className="p-3 text-right">Recipe Cost per Unit (Rs.)</th>
                <th className="p-3 text-right">Total Food Cost (Rs.)</th>
                <th className="p-3 text-right">Gross Profit (Rs.)</th>
                <th className="p-3 text-center">GP %</th>
                <th className="p-3 text-center">Food Cost %</th>
              </tr>
            </thead>
            <tbody className="divide-y divide-gray-200 font-medium text-gray-800">
              {filteredProducts.length === 0 ? (
                <tr>
                  <td colSpan="11" className="p-8 text-center text-gray-500">
                    No product record found for the selected filter.
                  </td>
                </tr>
              ) : (
                filteredProducts.map((p, idx) => {
                  const gpPercentage =
                    p.gpPct ?? (p.totalRevenue > 0 ? (p.grossProfit / p.totalRevenue) * 100 : 0);
                  const foodCostPercentage =
                    p.foodCostPct ?? (p.totalRevenue > 0 ? (p.totalCOGS / p.totalRevenue) * 100 : 0);

                  return (
                    <tr
                      key={p.productId || idx}
                      className="hover:bg-slate-50 transition-colors odd:bg-white even:bg-slate-50/40"
                    >
                      <td className="p-3 font-mono font-bold text-gray-700">
                        {p.productCode || p.sku || 'N/A'}
                      </td>
                      <td className="p-3 font-bold text-gray-900">{p.productName}</td>
                      <td className="p-3 text-gray-600">
                        <span className="px-2 py-0.5 bg-gray-100 border border-gray-200 rounded text-[10px] font-bold text-gray-700">
                          {p.category || 'General'}
                        </span>
                      </td>
                      <td className="p-3 text-center font-bold text-gray-900">{p.unitsSold}</td>
                      <td className="p-3 text-right font-semibold text-gray-800">
                        Rs. {(p.avgSellingPrice || 0).toLocaleString('en-US', { minimumFractionDigits: 2 })}
                      </td>
                      <td className="p-3 text-right font-black text-gray-900">
                        Rs. {(p.totalRevenue || 0).toLocaleString('en-US', { minimumFractionDigits: 2 })}
                      </td>
                      <td className="p-3 text-right text-slate-700 font-semibold">
                        Rs. {(p.unitCost || p.recipeCost || 0).toLocaleString('en-US', { minimumFractionDigits: 2 })}
                      </td>
                      <td className="p-3 text-right font-bold text-slate-800">
                        Rs. {(p.totalCOGS || 0).toLocaleString('en-US', { minimumFractionDigits: 2 })}
                      </td>
                      <td
                        className={`p-3 text-right font-black ${
                          p.grossProfit < 0 ? 'text-rose-600' : 'text-emerald-600'
                        }`}
                      >
                        Rs. {(p.grossProfit || 0).toLocaleString('en-US', { minimumFractionDigits: 2 })}
                      </td>
                      <td className="p-3 text-center">
                        <span
                          className={`px-2 py-0.5 rounded text-[11px] font-bold ${
                            gpPercentage < 20
                              ? 'bg-rose-100 text-rose-800'
                              : gpPercentage < 50
                              ? 'bg-amber-100 text-amber-800'
                              : 'bg-emerald-100 text-emerald-800'
                          }`}
                        >
                          {gpPercentage.toFixed(1)}%
                        </span>
                      </td>
                      <td className="p-3 text-center font-bold text-slate-700">
                        {foodCostPercentage.toFixed(1)}%
                      </td>
                    </tr>
                  );
                })
              )}
            </tbody>

            {/* Total Summary Footer Row */}
            {filteredProducts.length > 0 && (
              <tfoot className="bg-slate-900 text-white font-black text-xs border-t-2 border-slate-700 print:bg-gray-300 print:text-black">
                <tr>
                  <td className="p-3" colSpan="3">
                    REPORT TOTAL / AVERAGE SUMMARY
                  </td>
                  <td className="p-3 text-center text-amber-400 font-black">{totals.totalQty}</td>
                  <td className="p-3 text-right text-slate-200">
                    Rs. {(totals.avgSellingPrice || 0).toFixed(2)}
                  </td>
                  <td className="p-3 text-right text-blue-300 text-sm">
                    Rs. {totals.totalSalesValue.toLocaleString('en-US', { minimumFractionDigits: 2 })}
                  </td>
                  <td className="p-3 text-right text-slate-300">
                    Rs. {(totals.avgRecipeCost || 0).toFixed(2)}
                  </td>
                  <td className="p-3 text-right text-amber-300 text-sm">
                    Rs. {totals.totalFoodCost.toLocaleString('en-US', { minimumFractionDigits: 2 })}
                  </td>
                  <td className="p-3 text-right text-emerald-400 text-sm">
                    Rs. {totals.totalGrossProfit.toLocaleString('en-US', { minimumFractionDigits: 2 })}
                  </td>
                  <td className="p-3 text-center text-emerald-300">{totals.overallGP.toFixed(1)}%</td>
                  <td className="p-3 text-center text-amber-300">{totals.overallFoodCostPct.toFixed(1)}%</td>
                </tr>
              </tfoot>
            )}
          </table>
        </div>
      </div>
    </div>
  );
};

export default ProductFoodCostReportTab;
